import {
  EnvironmentRegistryEntry,
  TenantAppEnvironment,
  TenantAuthoringEnvironment,
  TenantGeneratedSchemaEntry,
  DEFAULT_ALLOWED_URI_PROTOCOLS,
  ensureArray,
} from '@web-config-app/core';
import { ConfigTenantId, GeneratedConfigTenantId } from '../../types';
import GeneratedTenantConfigs from './json/tenants.json';
import GeneratedEnvironmentConfigs from './json/environments.json';

// Annotated, not cast, so the generated JSON is checked against the type. Entries have no
// `id` (it's their key), and `| undefined` makes callers handle an id that isn't registered.
const environmentRegistry: Record<
  string,
  Omit<EnvironmentRegistryEntry, 'id'> | undefined
> = GeneratedEnvironmentConfigs;

/**
 * Resolves one preview environment reference against the registry, merging in the
 * tenant-level overrides.
 *
 * Returns undefined for a dangling reference (a renamed or retired id) rather than throwing:
 * there's no catch between here and useAccessibleTenants, so a throw would take down the
 * tenant switcher for every tenant, not just the one with the bad reference.
 */
function resolvePreviewEnvironment(
  previewEnvironment: TenantAuthoringEnvironment,
) {
  const previewEnvironmentConfig = environmentRegistry[previewEnvironment.id];

  if (!previewEnvironmentConfig) return undefined;

  return {
    ...previewEnvironmentConfig,
    id: previewEnvironment.id,
    defaultAuthoringEnvironment: previewEnvironment.defaultAuthoringEnvironment,
    publishDangerously:
      previewEnvironment.publishDangerously ||
      previewEnvironmentConfig.publishDangerously,
  };
}

/**
 * A preview environment reference merged with its registry config. NonNullable because
 * callers only ever see entries resolvePreviewEnvironmentInput has already filtered.
 */
export type ResolvedPreviewEnvironment = NonNullable<
  ReturnType<typeof resolvePreviewEnvironment>
>;

/**
 * Resolves a tenant's previewEnvironment input -- one environment or a list -- to a list,
 * always.
 *
 * The object-or-array union is how tenants *declare* previews, and it stops at this boundary:
 * nothing downstream needs an Array.isArray check. Entries without an id are dropped as well
 * as unresolvable ones -- the registry is untyped JSON at runtime, so that's real defense.
 */
function resolvePreviewEnvironmentInput(
  previewEnvironmentInput:
    | TenantAuthoringEnvironment
    | TenantAuthoringEnvironment[]
    | undefined,
): ResolvedPreviewEnvironment[] {
  return ensureArray(previewEnvironmentInput)
    .filter((previewEnvironment) => previewEnvironment?.id)
    .map(resolvePreviewEnvironment)
    .filter(
      (
        resolvedEnvironment,
      ): resolvedEnvironment is ResolvedPreviewEnvironment =>
        Boolean(resolvedEnvironment),
    );
}

/**
 *  @returns the environment object for the given env key from `./generated/environments.json`
 */
function getGeneratedEnvironmentConfig(
  tenantEnvironment: TenantAppEnvironment,
) {
  const baseEnvironmentConfig =
    environmentRegistry[tenantEnvironment.baseEnvironment.id];

  // Can't degrade the way a dangling preview does -- drop the base and the tenant has
  // nothing to author against. Throws rather than falling through to reading `auth0` off
  // undefined, which names neither the tenant nor the bad id. getTenantConfig catches it and
  // skips the tenant, so this doesn't reach the switcher.
  if (!baseEnvironmentConfig) {
    throw new Error(
      `Base environment "${tenantEnvironment.baseEnvironment.id}" is not in the generated environment registry. Check the id against .config-registry/environments/ and regenerate configs.`,
    );
  }

  const previewEnvironments = resolvePreviewEnvironmentInput(
    tenantEnvironment.previewEnvironment,
  );

  // The raw `previewEnvironment` is dropped: leaving it beside the resolved list invites a
  // reader to pick the wrong one.
  const {
    previewEnvironment: unresolvedPreviewEnvironment,
    ...restOfEnvironment
  } = tenantEnvironment;

  const appEnvironment = {
    ...restOfEnvironment,
    auth0: {
      organizationId: tenantEnvironment.auth0.organizationId,
      clientId:
        tenantEnvironment.auth0.clientId ||
        baseEnvironmentConfig.auth0.clientId,
      domain:
        tenantEnvironment.auth0.domain || baseEnvironmentConfig.auth0.domain,
      audience: baseEnvironmentConfig.auth0.primaryAudience,
    },
    baseEnvironment: {
      ...baseEnvironmentConfig,
      id: tenantEnvironment.baseEnvironment.id,
      defaultAuthoringEnvironment:
        tenantEnvironment.baseEnvironment?.defaultAuthoringEnvironment,
      publishDangerously:
        tenantEnvironment.baseEnvironment.publishDangerously ||
        baseEnvironmentConfig.publishDangerously,
    },
    previewEnvironments,
  };
  return appEnvironment;
}

export function getGeneratedEnvironmentsConfigForTenant(
  tenantId: ConfigTenantId<GeneratedConfigTenantId>,
) {
  const tenantData: TenantGeneratedSchemaEntry = GeneratedTenantConfigs[
    tenantId
  ] as TenantGeneratedSchemaEntry;

  const tenantWithEnvironment = {
    ...tenantData,
    appEnvironment: {
      production: getGeneratedEnvironmentConfig(
        tenantData.appEnvironment.production,
      ),
      staging: getGeneratedEnvironmentConfig(tenantData.appEnvironment.staging),
    },
  };

  const uriSchemePolicy = tenantData.frontendValidation?.uriSchemePolicy;

  // if allow list is provided, merge with default list (unless override is true) and dedupe
  if (
    uriSchemePolicy &&
    Array.isArray(uriSchemePolicy.allowed) &&
    !uriSchemePolicy.overrideDefaultAllowedList
  ) {
    const merged = [
      ...DEFAULT_ALLOWED_URI_PROTOCOLS,
      ...uriSchemePolicy.allowed,
    ]
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const deduped = Array.from(new Set(merged));

    return {
      ...tenantWithEnvironment,
      frontendValidation: {
        ...tenantWithEnvironment.frontendValidation,
        uriSchemePolicy: { ...uriSchemePolicy, allowed: deduped },
      },
    };
  }

  // if allow list is provided and override is true, use only the provided list
  if (
    uriSchemePolicy &&
    Array.isArray(uriSchemePolicy.allowed) &&
    uriSchemePolicy.overrideDefaultAllowedList
  ) {
    return {
      ...tenantWithEnvironment,
      frontendValidation: {
        ...tenantWithEnvironment.frontendValidation,
        uriSchemePolicy: {
          ...uriSchemePolicy,
          allowed: uriSchemePolicy.allowed,
        },
      },
    };
  }

  // If no tenant uri policy provided, just return config with default list.
  return {
    ...tenantWithEnvironment,
    frontendValidation: {
      ...tenantWithEnvironment.frontendValidation,
      uriSchemePolicy: { allowed: DEFAULT_ALLOWED_URI_PROTOCOLS },
    },
  };
}
